/**
 * Single source of truth for how a player's name is rendered across the UI.
 *
 *   Team UI (lineup panels, edit dialogs, player rows, dropdowns):
 *     first = player_short_first_name → player_first_name → ""
 *     last  = player_short_last_name  → player_last_name  → ""
 *     full  = `${first} ${last}`.trim()
 *
 *   Events / broker / scorers / timeline:
 *     name = unique_name → player_short_last_name → player_last_name → ""
 *
 * Falls back gracefully when a field is missing. Always returns a string.
 */

const pick = (...candidates) => {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return "";
};

export const getTeamFirstName = (player) =>
  pick(player?.player_short_first_name, player?.player_first_name);

export const getTeamLastName = (player) =>
  pick(player?.player_short_last_name, player?.player_last_name);

/**
 * The display string used in the lineup / squad UI. First + last (per the
 * priority order above), with sensible fallbacks if both are blank.
 */
export const getTeamFullName = (player) => {
  if (!player) return "";
  const first = getTeamFirstName(player);
  const last = getTeamLastName(player);
  const composed = `${first} ${last}`.trim();
  if (composed) return composed;
  // Last-ditch fallbacks so we never render an empty cell.
  return pick(
    player.player_short_name,
    player.player_name,
    player.name,
  );
};

/**
 * The display string used in events (timeline, scorer banners, broker
 * payloads, card overlays, notifications). Single token — no first name.
 */
export const getEventName = (player) => {
  if (!player) return "";
  return pick(
    player.unique_name,
    player.player_short_last_name,
    player.player_last_name,
    // Fallbacks for legacy / partial event records.
    player.player_short_name,
    player.player_name,
    player.name,
  );
};

/**
 * The player's second name and shirt number as an event payload carries them
 * — `strSecondName` / `strShirtNumber`, or with a suffix (`Off` / `On`) for a
 * substitution's two players. Only the ones with a value are returned, so a
 * missing player adds nothing.
 */
export const playerKeys = (player, suffix = "") => {
  const keys = {};
  const name = getEventName(player);
  const shirt = player?.shirt_number;
  if (name) keys[`strSecondName${suffix}`] = name;
  if (shirt !== undefined && shirt !== null && shirt !== "") keys[`strShirtNumber${suffix}`] = String(shirt);
  return keys;
};

/* ============================================================
 * Staff (coaches, managers, etc.)
 *
 * Same priority order as team UI players, but on staff_* fields:
 *   first = staff_first_name → ""
 *   last  = staff_last_name  → ""
 *   full  = `${first} ${last}`.trim()
 *
 * Some upstream payloads also surface a flat staff_short_name — kept as a
 * final fallback so we never render an empty cell.
 * ============================================================ */

export const getStaffFirstName = (staff) =>
  pick(staff?.staff_first_name);

export const getStaffLastName = (staff) =>
  pick(staff?.staff_last_name);

export const getStaffFullName = (staff) => {
  if (!staff) return "";
  const first = getStaffFirstName(staff);
  const last = getStaffLastName(staff);
  const composed = `${first} ${last}`.trim();
  if (composed) return composed;
  return pick(staff.staff_short_name, staff.staff_name, staff.name);
};

/* ============================================================
 * Officials (referees, assistants, fourth official, VAR)
 *
 *   first = official_short_first_name → official_first_name → ""
 *   last  = official_short_last_name  → official_last_name  → ""
 *
 * In event-context (timeline label, broker payload) we use the same
 * surname-priority pattern as players, scoped to the official_* fields.
 * ============================================================ */

export const getOfficialFirstName = (official) =>
  pick(official?.official_short_first_name, official?.official_first_name);

export const getOfficialLastName = (official) =>
  pick(official?.official_short_last_name, official?.official_last_name);

export const getOfficialFullName = (official) => {
  if (!official) return "";
  const first = getOfficialFirstName(official);
  const last = getOfficialLastName(official);
  const composed = `${first} ${last}`.trim();
  if (composed) return composed;
  return pick(
    official.official_short_name,
    official.official_name,
    official.name,
  );
};

export const getOfficialEventName = (official) => {
  if (!official) return "";
  return pick(
    official.unique_name,
    official.official_short_last_name,
    official.official_last_name,
    official.official_short_name,
    official.official_name,
    official.name,
  );
};
